// frontend/src/components/reorderLevels/SetLevelsDialog.jsx
//
// The small form behind "Set for all" and "Set for selected": one Minimum
// stock, one Expected monthly sales, either or both. It reads the two numbers
// and hands over the filled ones; what applying them means is the caller's.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';

import DialogErrorBanner from '../common/DialogErrorBanner';
import { useFormatters } from '../../hooks/useFormatters';
import { parseClearableUnitsCell } from '../../utils/planningCellParsers';
import '../../styles/dialog-form.css';

export default function SetLevelsDialog({
  open,
  title,
  // The sentence naming the rows the values go to.
  scope,
  applying = false,
  error = null,
  // ({ min_inventory_units?, expected_monthly_sales? }) => void — only the
  // filled fields, as whole numbers.
  onApply,
  onCancel,
}) {
  return (
    <Dialog
      open={open}
      onClose={applying ? undefined : onCancel}
      className="dialog-form dialog-form--confirm"
    >
      <DialogTitle>{title}</DialogTitle>
      {/* Keyed on `open` so the fields start empty on every opening, while
          the form stays in place through the fade-out. */}
      <SetLevelsForm
        key={String(open)}
        scope={scope}
        applying={applying}
        // A fading popup has been answered: its controls no longer act.
        locked={applying || !open}
        error={error}
        onApply={onApply}
        onCancel={onCancel}
      />
    </Dialog>
  );
}

function SetLevelsForm({ scope, applying, locked, error, onApply, onCancel }) {
  const { t } = useTranslation();
  const { parseNumber } = useFormatters();
  const [minimumInput, setMinimumInput] = useState('');
  const [salesInput, setSalesInput] = useState('');

  // A typed box read the way the grid reads a typed cell: blank is null (the
  // field is not filled), text that is no number is NaN.
  const read = (raw) => parseClearableUnitsCell({ newValue: raw.trim(), oldValue: NaN }, parseNumber);
  const minimum = read(minimumInput);
  const sales = read(salesInput);
  const canApply = !Number.isNaN(minimum) && !Number.isNaN(sales)
    && (minimum !== null || sales !== null);

  const handleApply = () => {
    const values = {};
    if (minimum !== null) values.min_inventory_units = minimum;
    if (sales !== null) values.expected_monthly_sales = sales;
    onApply(values);
  };

  return (
    <>
      <DialogContent>
        <TextField
          size="small"
          autoFocus
          label={t('inventory.columns.minimumStock')}
          value={minimumInput}
          onChange={(e) => setMinimumInput(e.target.value)}
          error={Number.isNaN(minimum)}
          disabled={locked}
        />
        <TextField
          size="small"
          label={t('reorderLevels.columns.expectedMonthlySales')}
          value={salesInput}
          onChange={(e) => setSalesInput(e.target.value)}
          error={Number.isNaN(sales)}
          disabled={locked}
        />
        <Typography variant="body2" color="text.secondary">{scope}</Typography>
        <DialogErrorBanner error={error} />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCancel} disabled={locked}>
          {t('btn.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!canApply || locked}
          loading={applying}
        >
          {t('btn.apply')}
        </Button>
      </DialogActions>
    </>
  );
}
